import { FC, useEffect, useState } from 'react';
import { CallGroupTypes } from '@frontend/api-call-group';
import { Slider } from '@frontend/components';
import { theme } from '@frontend/theme';
import { getDeviceKey } from './device-key';

interface Props {
  device: CallGroupTypes.Device;
  isMobilePhone: boolean;
  devices: CallGroupTypes.Device[];
  deviceKey: string;
  onRingChange: (deviceKey: string, delayStart: number, timeout: number) => void;
}

export const RingSlider: FC<React.PropsWithChildren<Props>> = ({
  device,
  isMobilePhone,
  devices,
  deviceKey,
  onRingChange,
}) => {
  const initialSliderValues = isMobilePhone
    ? [device.timeout / 1000]
    : [device.delayStart / 1000, (device.timeout + device.delayStart) / 1000];

  const [sliderValues, setSliderValues] = useState<number[]>(initialSliderValues);
  const [refreshKey, setRefreshKey] = useState(0);

  // Ensures sliderValues are reset to initialSliderValues when device settings or pagination change.
  useEffect(() => {
    setSliderValues(initialSliderValues);
  }, [isMobilePhone, device.timeout, device.delayStart]);

  const handleSliderChange = (dKey: string, values: number[]) => {
    const selectedStartValue = values[0] * 1000;
    const selectedEndValue = values[1] * 1000;

    const selectedDevice = devices.find((device) => getDeviceKey(device) === dKey);
    const selectedDeviceTimeValues = selectedDevice
      ? [[selectedDevice.delayStart, selectedDevice.delayStart + selectedDevice.timeout]]
      : [];
    const filteredDeviceTimeValues = devices
      .filter((device) => getDeviceKey(device) !== dKey)
      .map(({ delayStart, timeout }) => [delayStart, delayStart + timeout]);

    // Extract the start and end times for the selected device
    const [[selectedDeviceStartValue, selectedDeviceEndValue]] = selectedDeviceTimeValues;

    // Compute a valid end time value to avoid overlaps or gaps
    // If the selected device range exceeds all existing ranges, return the earliest start time
    const validEndValue =
      selectedDeviceStartValue < Math.min(...filteredDeviceTimeValues.map(([start]) => start)) &&
      selectedDeviceEndValue > Math.max(...filteredDeviceTimeValues.map(([, end]) => end))
        ? Math.min(...filteredDeviceTimeValues.map(([start]) => start))
        : filteredDeviceTimeValues
            .filter(([start, end]) => start <= selectedDeviceEndValue && end >= selectedDeviceEndValue)
            .reduce((min, [start]) => Math.min(min, start), Infinity);

    // Ensure the end time value avoids any gaps between device ring times
    const endTimeValue = Math.max(
      Number.isFinite(validEndValue) ? validEndValue : selectedDeviceStartValue,
      selectedEndValue
    );

    // Compute a valid start time value to avoid overlaps or gaps
    const validStartValue = filteredDeviceTimeValues.reduce((max, [start, end]) => {
      const allEndValues = filteredDeviceTimeValues.map(([, end]) => end);
      const highestEndValue = Math.max(...allEndValues);
      const allStartValues = filteredDeviceTimeValues.map(([start]) => start);

      if (selectedDeviceStartValue < Math.min(...allStartValues) && selectedDeviceEndValue > highestEndValue) {
        return Math.max(max, highestEndValue);
      }

      if (start <= selectedDeviceStartValue && end >= selectedDeviceStartValue) {
        const overlappingEndValue = filteredDeviceTimeValues
          .filter(([otherStart, otherEnd]) => end >= otherStart && end <= otherEnd)
          .reduce((maxEnd, [, otherEnd]) => Math.max(maxEnd, otherEnd), -Infinity);

        return Math.max(max, overlappingEndValue);
      }

      return max;
    }, -Infinity);

    // Ensure the start time value avoids any gaps or overlaps
    const startTimeValue = Number.isFinite(validStartValue)
      ? Math.min(validStartValue, selectedStartValue)
      : selectedStartValue;

    const newDelayStart = isMobilePhone ? device.delayStart : startTimeValue;
    const newTimeout = isMobilePhone ? selectedStartValue : endTimeValue - startTimeValue;

    onRingChange(dKey, newDelayStart, newTimeout);
    if (isMobilePhone) {
      setSliderValues([selectedStartValue / 1000]);
    } else {
      setSliderValues([startTimeValue / 1000, endTimeValue / 1000]);
    }
    setRefreshKey((prevKey) => prevKey + 1);
  };

  return (
    <Slider
      key={refreshKey}
      max={60}
      min={0}
      onChange={(values) => handleSliderChange(deviceKey, [...values])}
      step={6}
      style={{ margin: theme.spacing(3, 0, 2), width: '85%' }}
      values={sliderValues || []}
    />
  );
};
